import { ChevronDown, Loader2 } from 'lucide-react'
import { Button } from '@/components/ui'
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
} from '@/components/ui'
import { useTasks } from '@/hooks'
import type { Task } from '@/api'

interface TaskSelectorProps {
  selectedTask: Task | null
  onSelect: (task: Task) => void
  disabled?: boolean
}

export function TaskSelector({ selectedTask, onSelect, disabled }: TaskSelectorProps) {
  // Fetch tasks that are in progress or pending (can work on)
  const { data, isLoading } = useTasks({
    per_page: 50,
  })

  // Filter to only show workable tasks
  const workableTasks = data?.items.filter(
    (task) => task.status === 'in_progress' || task.status === 'pending'
  ) ?? []

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          variant="outline"
          className="w-full justify-between text-left font-normal"
          disabled={disabled || isLoading}
        >
          {isLoading ? (
            <>
              <Loader2 className="h-4 w-4 animate-spin mr-2" />
              Loading tasks...
            </>
          ) : selectedTask ? (
            <span className="truncate">{selectedTask.title}</span>
          ) : (
            <span className="text-muted-foreground">Select a task...</span>
          )}
          <ChevronDown className="h-4 w-4 ml-2 shrink-0 opacity-50" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent className="w-[300px] max-h-[300px] overflow-y-auto" align="start">
        <DropdownMenuLabel>Select Task</DropdownMenuLabel>
        <DropdownMenuSeparator />
        {workableTasks.length === 0 ? (
          <div className="px-2 py-4 text-sm text-center text-muted-foreground">
            No tasks available
          </div>
        ) : (
          workableTasks.map((task) => (
            <DropdownMenuItem
              key={task.id}
              onClick={() => onSelect(task)}
              className="flex flex-col items-start gap-1 cursor-pointer"
            >
              <span className="font-medium truncate w-full">{task.title}</span>
              <span className="text-xs text-muted-foreground">
                {task.client_name && `${task.client_name} · `}
                {task.status === 'in_progress' ? 'In Progress' : 'Pending'}
              </span>
            </DropdownMenuItem>
          ))
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  )
}
